import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/shared/PageHeader';
import { MetricCard } from '@/components/shared/MetricCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { cn } from '@/lib/utils';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import {
    Smartphone, Save, RefreshCw, Loader2, LogIn,
    CheckCircle, AlertTriangle, XCircle, Clock,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

// ─── Types ──────────────────────────────────────────────────────────────────

interface AppConfig {
    min_version?: { version: string; version_code: number; build_timestamp: number };
    recommended_version?: { version: string; version_code: number; build_timestamp: number };
    force_update_after_days?: number;
    play_store_url?: string;
}

interface LoginAttempt {
    id: string;
    user_id: string | null;
    device_model: string | null;
    android_version: string | null;
    app_version: string;
    version_code: number | null;
    build_hash: string;
    build_time: string;
    success: boolean;
    error_message: string | null;
    platform: string | null;
    created_at: string;
    display_name?: string | null;
    email?: string | null;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function AppVersionsPage() {
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState<'config' | 'logins'>('config');
    const [saving, setSaving] = useState(false);
    const [showOnlyErrors, setShowOnlyErrors] = useState(false);

    // Form state
    const [formMinVersion, setFormMinVersion] = useState('');
    const [formMinCode, setFormMinCode] = useState('');
    const [formMinTs, setFormMinTs] = useState('');
    const [formRecVersion, setFormRecVersion] = useState('');
    const [formRecCode, setFormRecCode] = useState('');
    const [formRecTs, setFormRecTs] = useState('');
    const [formForceDays, setFormForceDays] = useState('');
    const [formPlayStore, setFormPlayStore] = useState('');

    // ─── Load Config ───────────────────────────
    const { data: config, isLoading: configLoading } = useQuery({
        queryKey: ['admin', 'app-config'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<any>('admin_get_all_config');
            if (error) throw error;
            const raw = normalizeRpcResponse(data);
            const cfg: AppConfig = {};
            for (const item of raw) {
                if (item.key === 'min_version' && typeof item.value === 'object') {
                    cfg.min_version = item.value;
                } else if (item.key === 'recommended_version' && typeof item.value === 'object') {
                    cfg.recommended_version = item.value;
                } else if (item.key === 'force_update_after_days') {
                    cfg.force_update_after_days = typeof item.value === 'number' ? item.value : item.value?.days;
                } else if (item.key === 'play_store_url') {
                    cfg.play_store_url = typeof item.value === 'string' ? item.value : item.value?.url;
                }
            }

            // Populate form
            if (cfg.min_version) {
                setFormMinVersion(cfg.min_version.version);
                setFormMinCode(String(cfg.min_version.version_code));
                setFormMinTs(String(cfg.min_version.build_timestamp));
            }
            if (cfg.recommended_version) {
                setFormRecVersion(cfg.recommended_version.version);
                setFormRecCode(String(cfg.recommended_version.version_code));
                setFormRecTs(String(cfg.recommended_version.build_timestamp));
            }
            if (cfg.force_update_after_days != null) {
                setFormForceDays(String(cfg.force_update_after_days));
            }
            if (cfg.play_store_url) {
                setFormPlayStore(cfg.play_store_url);
            }
            return cfg;
        },
    });

    // ─── Load Logins ───────────────────────────
    const { data: loginData, isLoading: loginsLoading } = useQuery({
        queryKey: ['admin', 'login-attempts'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<LoginAttempt[]>('admin_get_login_attempts', { p_days: 7 });
            if (error) throw error;
            return normalizeRpcResponse(data);
        },
    });

    const logins = loginData || [];
    const loginStats = {
        total: logins.length,
        successful: logins.filter(a => a.success).length,
        failed: logins.filter(a => !a.success).length,
        rate: logins.length > 0 ? ((logins.filter(a => a.success).length / logins.length) * 100).toFixed(1) : '0',
    };
    const filteredLogins = showOnlyErrors ? logins.filter(a => !a.success) : logins;

    // ─── Save Config ───────────────────────────
    const handleSave = async () => {
        setSaving(true);
        try {
            const updates: Array<{ key: string; value: unknown }> = [];

            if (formMinVersion && formMinCode && formMinTs) {
                updates.push({
                    key: 'min_version',
                    value: { version: formMinVersion, version_code: Number(formMinCode), build_timestamp: Number(formMinTs) },
                });
            }
            if (formRecVersion && formRecCode && formRecTs) {
                updates.push({
                    key: 'recommended_version',
                    value: { version: formRecVersion, version_code: Number(formRecCode), build_timestamp: Number(formRecTs) },
                });
            }
            if (formForceDays) {
                updates.push({ key: 'force_update_after_days', value: Number(formForceDays) });
            }
            if (formPlayStore) {
                updates.push({ key: 'play_store_url', value: formPlayStore });
            }

            for (const u of updates) {
                const { error } = await dbClient.rpc('admin_upsert_app_config', {
                    p_key: u.key,
                    p_value: u.value,
                });
                if (error) throw error;
            }

            queryClient.invalidateQueries({ queryKey: ['admin', 'app-config'] });
        } catch (err) {
            console.error('Save config error:', err);
        } finally {
            setSaving(false);
        }
    };

    const TABS = [
        { value: 'config', label: 'Configuración', icon: <Smartphone size={14} /> },
        { value: 'logins', label: 'Intentos de Login', icon: <LogIn size={14} /> },
    ] as const;

    return (
        <div className="space-y-6">
            <PageHeader title="Versiones de la App" description="Force update, distribución de versiones e historial de login" />

            {/* Tabs */}
            <div className="flex gap-1 p-1 bg-muted/50 rounded-lg w-fit">
                {TABS.map(t => (
                    <button
                        key={t.value}
                        onClick={() => setActiveTab(t.value)}
                        className={cn(
                            'flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all',
                            activeTab === t.value
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        {t.icon}
                        {t.label}
                    </button>
                ))}
            </div>

            {/* ─── Config Tab ──────────────────────────────────────────── */}
            {activeTab === 'config' && (
                <div className="space-y-4">
                    {/* Current Config Summary */}
                    {config && (
                        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Configuración Actual</h3>
                            <div className="flex flex-wrap gap-2">
                                {config.min_version ? (
                                    <StatusBadge status={`Mín: ${config.min_version.version} (code ${config.min_version.version_code})`} variant="warning" />
                                ) : (
                                    <StatusBadge status="Sin versión mínima" variant="error" />
                                )}
                                {config.recommended_version ? (
                                    <StatusBadge status={`Rec: ${config.recommended_version.version}`} variant="info" />
                                ) : null}
                                {config.force_update_after_days ? (
                                    <StatusBadge status={`Force: ${config.force_update_after_days} días`} variant="neutral" />
                                ) : null}
                            </div>
                        </div>
                    )}

                    {/* Edit Form */}
                    <div className="rounded-xl border border-border bg-card p-5 space-y-5">
                        <h3 className="text-sm font-semibold text-foreground">Editar Configuración</h3>

                        {/* Min Version */}
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Versión Mínima Requerida</label>
                            <p className="text-xs text-muted-foreground mt-0.5 mb-3">Los usuarios con versiones inferiores verán un modal bloqueante.</p>
                            <div className="grid grid-cols-3 gap-3">
                                <InputField label="Versión" placeholder="2.2.3" value={formMinVersion} onChange={setFormMinVersion} />
                                <InputField label="Version Code" placeholder="205" type="number" value={formMinCode} onChange={setFormMinCode} />
                                <div>
                                    <label className="text-[10px] text-muted-foreground">Build Timestamp</label>
                                    <div className="flex gap-1 mt-1">
                                        <input
                                            type="number"
                                            placeholder="1738000000000"
                                            value={formMinTs}
                                            onChange={e => setFormMinTs(e.target.value)}
                                            className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                                        />
                                        <button
                                            onClick={() => setFormMinTs(String(Date.now()))}
                                            className="px-2 py-2 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground transition-colors"
                                        >
                                            Ahora
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-border" />

                        {/* Recommended Version */}
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Versión Recomendada (opcional)</label>
                            <p className="text-xs text-muted-foreground mt-0.5 mb-3">Banner no bloqueante sugiriendo actualizar.</p>
                            <div className="grid grid-cols-3 gap-3">
                                <InputField label="Versión" placeholder="2.3.0" value={formRecVersion} onChange={setFormRecVersion} />
                                <InputField label="Version Code" placeholder="210" type="number" value={formRecCode} onChange={setFormRecCode} />
                                <div>
                                    <label className="text-[10px] text-muted-foreground">Build Timestamp</label>
                                    <div className="flex gap-1 mt-1">
                                        <input
                                            type="number"
                                            placeholder="1738000000000"
                                            value={formRecTs}
                                            onChange={e => setFormRecTs(e.target.value)}
                                            className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                                        />
                                        <button
                                            onClick={() => setFormRecTs(String(Date.now()))}
                                            className="px-2 py-2 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground transition-colors"
                                        >
                                            Ahora
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-border" />

                        {/* Force Update + Play Store */}
                        <div className="grid grid-cols-2 gap-4">
                            <InputField label="Días para forzar actualización" placeholder="30" type="number" value={formForceDays} onChange={setFormForceDays} />
                            <InputField label="URL Google Play Store" placeholder="https://play.google.com/..." value={formPlayStore} onChange={setFormPlayStore} />
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-2">
                            <button
                                onClick={handleSave}
                                disabled={saving || configLoading}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                            >
                                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                                {saving ? 'Guardando...' : 'Guardar Configuración'}
                            </button>
                            <button
                                onClick={() => queryClient.invalidateQueries({ queryKey: ['admin', 'app-config'] })}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
                            >
                                <RefreshCw size={14} />
                                Recargar
                            </button>
                        </div>
                    </div>

                    {/* Info Box */}
                    <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-2">
                        <p className="text-xs font-semibold text-amber-400 flex items-center gap-1.5">
                            <AlertTriangle size={13} />
                            Información Importante
                        </p>
                        <ul className="text-xs text-muted-foreground space-y-1">
                            <li>• Los cambios se aplican después de 5 minutos (tiempo de caché).</li>
                            <li>• Las actualizaciones críticas bloquean el acceso hasta que el usuario actualice.</li>
                            <li>• El force_update_after_days solo aplica entre versión mínima y recomendada.</li>
                        </ul>
                    </div>
                </div>
            )}

            {/* ─── Logins Tab ──────────────────────────────────────────── */}
            {activeTab === 'logins' && (
                <div className="space-y-4">
                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-3">
                        <MetricCard title="Total (7d)" value={loginStats.total} icon={<LogIn size={16} />} />
                        <MetricCard title="Exitosos" value={loginStats.successful} icon={<CheckCircle size={16} />} />
                        <MetricCard title="Fallidos" value={loginStats.failed} icon={<XCircle size={16} />} />
                        <MetricCard title="Tasa Éxito" value={`${loginStats.rate}%`} icon={<Clock size={16} />} />
                    </div>

                    {/* Filter */}
                    <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showOnlyErrors}
                                onChange={e => setShowOnlyErrors(e.target.checked)}
                                className="rounded border-border w-3.5 h-3.5 accent-primary"
                            />
                            <span className="text-xs text-foreground">Solo errores</span>
                        </label>
                        <span className="text-xs text-muted-foreground">| {filteredLogins.length} resultados</span>
                    </div>

                    {/* Logins Table */}
                    <div className="rounded-xl border border-border bg-card overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-border bg-muted/30">
                                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Usuario</th>
                                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground">Versión</th>
                                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground">Plataforma</th>
                                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground">Estado</th>
                                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground">Fecha</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {loginsLoading ? (
                                        <tr><td colSpan={5} className="py-12 text-center text-muted-foreground">
                                            <RefreshCw size={18} className="animate-spin mx-auto" />
                                        </td></tr>
                                    ) : filteredLogins.length === 0 ? (
                                        <tr><td colSpan={5} className="py-12 text-center text-muted-foreground text-xs">No hay intentos de login</td></tr>
                                    ) : (
                                        filteredLogins.slice(0, 100).map(login => (
                                            <tr key={login.id} className={cn('hover:bg-muted/20 transition-colors', !login.success && 'bg-red-500/5')}>
                                                <td className="px-4 py-2">
                                                    <p className="text-xs font-medium text-foreground">{login.display_name || login.email || 'Anónimo'}</p>
                                                    {login.user_id && <p className="text-[10px] text-muted-foreground font-mono truncate max-w-[120px]">{login.user_id.slice(0, 8)}...</p>}
                                                </td>
                                                <td className="px-3 py-2">
                                                    <span className="text-xs">{login.app_version || '?'}</span>
                                                    {login.version_code != null && <span className="text-[10px] text-muted-foreground ml-1">(c{login.version_code})</span>}
                                                </td>
                                                <td className="px-3 py-2">
                                                    <span className="text-xs">{login.platform || 'web'}</span>
                                                    {login.device_model && <span className="text-[10px] text-muted-foreground block">{login.device_model}</span>}
                                                </td>
                                                <td className="px-3 py-2">
                                                    <StatusBadge
                                                        status={login.success ? 'OK' : 'Error'}
                                                        variant={login.success ? 'success' : 'error'}
                                                    />
                                                    {login.error_message && <p className="text-[10px] text-red-400 mt-0.5 truncate max-w-[150px]">{login.error_message}</p>}
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    <span className="text-xs text-muted-foreground">
                                                        {formatDistanceToNow(new Date(login.created_at), { addSuffix: true, locale: es })}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Helper ─────────────────────────────────────────────────────────────────

function InputField({ label, placeholder, value, onChange, type = 'text' }: {
    label: string;
    placeholder: string;
    value: string;
    onChange: (v: string) => void;
    type?: string;
}) {
    return (
        <div>
            <label className="text-[10px] text-muted-foreground">{label}</label>
            <input
                type={type}
                placeholder={placeholder}
                value={value}
                onChange={e => onChange(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            />
        </div>
    );
}
